import type { AddressFields, ValidationResult } from "../types/validation";

// eslint-disable-next-line security/detect-unsafe-regex
const US_ZIP_REGEX = /^\d{5}(-\d{4})?$/;
const CA_POSTAL_REGEX = /^[A-Z]\d[A-Z]\s?\d[A-Z]\d$/i;
const AU_POSTAL_REGEX = /^\d{4}$/;

/**
 * Validate address fields.
 * Checks required fields and postal code format.
 */
export function validateAddress(
  address: Partial<AddressFields>,
): ValidationResult {
  if (!address.addressLine1 || address.addressLine1.trim().length < 5) {
    return {
      isValid: false,
      error: "Please enter a complete address (minimum 5 characters)",
    };
  }

  if (!address.addressCity || address.addressCity.trim().length === 0) {
    return {
      isValid: false,
      error: "City is required",
    };
  }

  if (!address.addressState || address.addressState.trim().length === 0) {
    return {
      isValid: false,
      error: "State/Province is required",
    };
  }

  if (!address.addressCountry || address.addressCountry.trim().length === 0) {
    return {
      isValid: false,
      error: "Country is required",
    };
  }

  const country = address.addressCountry.trim().toUpperCase();
  const isUS =
    country === "US" || country === "USA" || country === "UNITED STATES";
  const isCA = country === "CA" || country === "CANADA";
  const isAU = country === "AU" || country === "AUSTRALIA";

  if (!isUS && !isCA && !isAU) {
    return {
      isValid: false,
      error: "Country must be United States, Canada, or Australia",
    };
  }

  if (
    !address.addressPostalCode ||
    address.addressPostalCode.trim().length === 0
  ) {
    return {
      isValid: false,
      error: "Postal code is required",
    };
  }

  const postalCode = address.addressPostalCode.trim();

  if (isUS && !US_ZIP_REGEX.test(postalCode)) {
    return {
      isValid: false,
      error: "Invalid US ZIP code format (expected: 12345 or 12345-6789)",
    };
  }

  if (isCA && !CA_POSTAL_REGEX.test(postalCode)) {
    return {
      isValid: false,
      error: "Invalid Canadian postal code format (expected: A1A 1A1)",
    };
  }

  if (isAU && !AU_POSTAL_REGEX.test(postalCode)) {
    return {
      isValid: false,
      error: "Invalid Australian postal code format (expected: 4 digits)",
    };
  }

  return { isValid: true };
}

/**
 * Validate a Canadian postal code.
 * @returns null if valid, error message if invalid
 */
export const canadaPostalCodeValidator = (
  postalCode: string,
): string | null => {
  if (CA_POSTAL_REGEX.test(postalCode)) {
    return null;
  }
  return "Please enter a valid postal code";
};
